// src/pages/FounderCommandCenter.jsx
// Main Founder Dashboard - Comprehensive Pilot Metrics
// Displays all 10 KPIs with trends, activity data, and drill-down capability

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  TrendingUp,
  AlertTriangle,
  DollarSign,
  ChevronRight,
  Activity,
  Shield,
  Zap,
  Target,
  BarChart3,
  Clock,
  CheckCircle2,
  XCircle,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  RefreshCw,
  Calendar,
  Phone,
  Mail,
  Upload,
  Heart,
  UserPlus,
  Eye,
  MessageSquare,
  FileText,
  Settings,
  ExternalLink,
  TrendingDown,
  Send,
  MousePointer,
  Percent,
  Bell,
  Bot
} from "lucide-react";
import {
  getPilotOverview,
  getPilotAdoption,
  getPilotClientQuality,
  getPilotValue,
  getPilotSystemHealth,
  getPilotKPIs,
  getPilotKPIHistory
} from "../api";

const PERIOD_OPTIONS = [
  { value: '7_days', label: 'Last 7 Days' },
  { value: '30_days', label: 'Last 30 Days' },
  { value: '90_days', label: 'Last 90 Days' }
];

export default function FounderCommandCenter() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('7_days');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [kpis, setKpis] = useState(null);
  const [kpiHistory, setKpiHistory] = useState([]);
  const [overview, setOverview] = useState(null);
  const [adoption, setAdoption] = useState(null);
  const [clientQuality, setClientQuality] = useState(null);
  const [value, setValue] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);

  const loadData = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const periodDays = period === '7_days' ? 7 : period === '30_days' ? 30 : 90;

      const [kpisRes, historyRes, overviewRes, adoptionRes, qualityRes, valueRes, healthRes] = await Promise.all([
        getPilotKPIs().catch(() => null),
        getPilotKPIHistory(periodDays).catch(() => ({ data: [] })),
        getPilotOverview({ period }),
        getPilotAdoption(),
        getPilotClientQuality(),
        getPilotValue({ period }),
        getPilotSystemHealth({ period: '24_hours' })
      ]);

      // Transform KPI data for easier access
      const kpiData = kpisRes?.data;
      if (kpiData) {
        // Convert kpis array to object for easy access
        const kpiMap = {};
        if (kpiData.kpis && Array.isArray(kpiData.kpis)) {
          kpiData.kpis.forEach(kpi => {
            kpiMap[kpi.id] = kpi;
          });
        }
        // Merge with supplementary data
        setKpis({
          snapshot_date: kpiData.snapshotDate,
          wau: kpiMap.wau?.value || 0,
          dau: kpiData.supplementary?.dau || 0,
          mau: kpiData.supplementary?.mau || 0,
          avg_active_days_per_week: kpiMap.avg_active_days?.value || 0,
          tasks_completed_per_agent_week: kpiMap.tasks_per_agent?.value || 0,
          trigger_action_conversion_rate: kpiMap.trigger_conversion?.value ? parseFloat(kpiMap.trigger_conversion.value) / 100 : 0,
          client_contact_rate: kpiMap.contact_rate?.value ? parseFloat(kpiMap.contact_rate.value) / 100 : 0,
          sms_response_rate: kpiMap.sms_response?.value ? parseFloat(kpiMap.sms_response.value) / 100 : 0,
          retention_saves_count: kpiMap.retention_saves?.value || 0,
          estimated_revenue_protected: kpiMap.revenue_protected?.value || 0,
          incidents_count: kpiMap.incidents?.value || 0,
          automations_sent: kpiMap.automations_sent?.value || 0,
          automation_delivery_rate: 0,
          total_clients: kpiData.supplementary?.totalClients || 0,
          at_risk_clients: kpiData.supplementary?.atRiskClients || 0,
          churned_clients: kpiData.supplementary?.churnedClients || 0,
          total_sms_sent: kpiData.supplementary?.totalSmsSent || 0,
          total_emails_sent: kpiData.supplementary?.totalEmailsSent || 0,
          total_calls_made: kpiData.supplementary?.totalCallsMade || 0,
          avg_lead_time_days: 0
        });
      } else {
        setKpis(null);
      }
      setKpiHistory(historyRes?.data || []);
      setOverview(overviewRes?.data);
      setAdoption(adoptionRes?.data);
      setClientQuality(qualityRes?.data);
      setValue(valueRes?.data);
      setSystemHealth(healthRes?.data);
      setError(null);
    } catch (err) {
      console.error('Failed to load pilot metrics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [period]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 text-lg">Loading Pilot Metrics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white border border-red-200 rounded-2xl shadow-lg p-8 max-w-md text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Access Denied</h2>
          <p className="text-slate-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercent = (value, decimals = 1) => {
    if (value === null || value === undefined) return '0%';
    return `${(value * 100).toFixed(decimals)}%`;
  };

  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Calculate trends from history
  const getTrend = (key) => {
    if (!kpiHistory || !Array.isArray(kpiHistory) || kpiHistory.length < 2) return null;
    const recent = kpiHistory[0]?.[key];
    const older = kpiHistory[kpiHistory.length - 1]?.[key];
    if (!recent || !older || older === 0) return null;
    return ((recent - older) / older * 100).toFixed(1);
  };

  // Mini sparkline component
  const Sparkline = ({ data, dataKey, color = 'blue' }) => {
    if (!data || !Array.isArray(data) || data.length < 2) return null;
    const values = data.map(d => d[dataKey] || 0).reverse();
    const max = Math.max(...values, 1);
    const min = Math.min(...values);
    const range = max - min || 1;

    const points = values.map((v, i) => {
      const x = (i / (values.length - 1)) * 60;
      const y = 20 - ((v - min) / range) * 18;
      return `${x},${y}`;
    }).join(' ');

    const colors = {
      blue: '#3b82f6',
      green: '#10b981',
      amber: '#f59e0b',
      violet: '#8b5cf6',
      cyan: '#06b6d4',
      rose: '#f43f5e'
    };

    return (
      <svg width="60" height="24" className="ml-auto">
        <polyline
          points={points}
          fill="none"
          stroke={colors[color] || colors.blue}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
                  <Target className="w-6 h-6 text-white" />
                </div>
                Pilot Metrics Dashboard
              </h1>
              <p className="text-slate-500 mt-1 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatDate()}
                {kpis?.snapshot_date && (
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full ml-2">
                    Data as of {new Date(kpis.snapshot_date).toLocaleDateString()}
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="bg-white border border-slate-300 text-slate-700 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                {PERIOD_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <button
                onClick={() => loadData(true)}
                disabled={refreshing}
                className="p-2 bg-white hover:bg-slate-50 border border-slate-300 rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 text-slate-600 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

        {/* ============================================ */}
        {/* 10 KEY PILOT KPIs - Hero Section */}
        {/* ============================================ */}
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-amber-500" />
            10 Key Pilot KPIs
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {/* 1. WAU - Weekly Active Users */}
            <KPICard
              icon={<Users className="w-5 h-5" />}
              label="Weekly Active Users"
              value={kpis?.wau || overview?.agents?.activeWeek || 0}
              subtitle={`${kpis?.dau || overview?.agents?.activeToday || 0} today`}
              trend={getTrend('wau')}
              color="blue"
              sparkline={<Sparkline data={kpiHistory} dataKey="wau" color="blue" />}
            />

            {/* 2. Avg Active Days per Week */}
            <KPICard
              icon={<Calendar className="w-5 h-5" />}
              label="Avg Days Active/Week"
              value={(kpis?.avg_active_days_per_week || 0).toFixed(1)}
              subtitle="per agent"
              trend={getTrend('avg_active_days_per_week')}
              color="cyan"
              sparkline={<Sparkline data={kpiHistory} dataKey="avg_active_days_per_week" color="cyan" />}
            />

            {/* 3. Tasks Completed per Agent */}
            <KPICard
              icon={<CheckCircle2 className="w-5 h-5" />}
              label="Tasks/Agent/Week"
              value={(kpis?.tasks_completed_per_agent_week || 0).toFixed(1)}
              subtitle="completed tasks"
              trend={getTrend('tasks_completed_per_agent_week')}
              color="green"
              sparkline={<Sparkline data={kpiHistory} dataKey="tasks_completed_per_agent_week" color="green" />}
            />

            {/* 4. Trigger → Action Rate */}
            <KPICard
              icon={<MousePointer className="w-5 h-5" />}
              label="Trigger → Action"
              value={formatPercent(kpis?.trigger_action_conversion_rate)}
              subtitle="conversion rate"
              trend={getTrend('trigger_action_conversion_rate')}
              color="amber"
              sparkline={<Sparkline data={kpiHistory} dataKey="trigger_action_conversion_rate" color="amber" />}
            />

            {/* 5. Client Contact Rate */}
            <KPICard
              icon={<Phone className="w-5 h-5" />}
              label="Client Contact Rate"
              value={formatPercent(kpis?.client_contact_rate)}
              subtitle="of book touched"
              trend={getTrend('client_contact_rate')}
              color="violet"
              sparkline={<Sparkline data={kpiHistory} dataKey="client_contact_rate" color="violet" />}
            />

            {/* 6. SMS Response Rate */}
            <KPICard
              icon={<MessageSquare className="w-5 h-5" />}
              label="SMS Response Rate"
              value={formatPercent(kpis?.sms_response_rate)}
              subtitle="inbound/outbound"
              trend={getTrend('sms_response_rate')}
              color="blue"
              sparkline={<Sparkline data={kpiHistory} dataKey="sms_response_rate" color="blue" />}
            />

            {/* 7. Retention Saves */}
            <KPICard
              icon={<Shield className="w-5 h-5" />}
              label="Retention Saves"
              value={kpis?.retention_saves_count || overview?.clients?.saved || 0}
              subtitle="clients saved"
              trend={getTrend('retention_saves_count')}
              color="green"
              sparkline={<Sparkline data={kpiHistory} dataKey="retention_saves_count" color="green" />}
            />

            {/* 8. Revenue Protected */}
            <KPICard
              icon={<DollarSign className="w-5 h-5" />}
              label="Revenue Protected"
              value={formatCurrency(kpis?.estimated_revenue_protected || overview?.revenue?.estimatedSaved)}
              subtitle="@ $694/client"
              trend={getTrend('estimated_revenue_protected')}
              color="emerald"
              large
              sparkline={<Sparkline data={kpiHistory} dataKey="estimated_revenue_protected" color="green" />}
            />

            {/* 9. Automations Sent */}
            <KPICard
              icon={<Bot className="w-5 h-5" />}
              label="Automations Sent"
              value={kpis?.automations_sent || value?.automations?.sent || 0}
              subtitle={`${formatPercent(kpis?.automation_delivery_rate)} delivered`}
              trend={getTrend('automations_sent')}
              color="violet"
              sparkline={<Sparkline data={kpiHistory} dataKey="automations_sent" color="violet" />}
            />

            {/* 10. System Incidents */}
            <KPICard
              icon={<AlertTriangle className="w-5 h-5" />}
              label="Incidents"
              value={kpis?.incidents_count || 0}
              subtitle="this period"
              trend={getTrend('incidents_count')}
              trendInverse
              color={kpis?.incidents_count > 0 ? 'rose' : 'green'}
            />
          </div>
        </div>

        {/* ============================================ */}
        {/* CLIENT OVERVIEW SECTION */}
        {/* ============================================ */}
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            Client Overview
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard
              label="Total Clients"
              value={(kpis?.total_clients || overview?.clients?.total || 0).toLocaleString()}
              icon={<Users className="w-4 h-4" />}
              color="blue"
            />
            <StatCard
              label="At-Risk"
              value={kpis?.at_risk_clients || overview?.clients?.atRisk || 0}
              icon={<AlertTriangle className="w-4 h-4" />}
              color="amber"
            />
            <StatCard
              label="Churned"
              value={kpis?.churned_clients || overview?.clients?.churned || 0}
              icon={<XCircle className="w-4 h-4" />}
              color="rose"
            />
            <StatCard
              label="SMS Sent"
              value={kpis?.total_sms_sent || 0}
              icon={<MessageSquare className="w-4 h-4" />}
              color="blue"
            />
            <StatCard
              label="Emails Sent"
              value={kpis?.total_emails_sent || 0}
              icon={<Mail className="w-4 h-4" />}
              color="violet"
            />
            <StatCard
              label="Calls Made"
              value={kpis?.total_calls_made || 0}
              icon={<Phone className="w-4 h-4" />}
              color="green"
            />
          </div>
        </div>

        {/* ============================================ */}
        {/* MAIN GRID - Existing Sections */}
        {/* ============================================ */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Agent Adoption */}
          <SectionCard
            title="Agent Onboarding"
            subtitle={`${overview?.agents?.total || 0} total agents`}
            icon={<Users className="w-5 h-5 text-blue-600" />}
            onClick={() => navigate('/founder/adoption')}
            actionLabel="View All"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-blue-50 rounded-xl">
                <div className="text-2xl font-bold text-blue-700">{overview?.agents?.total || 0}</div>
                <div className="text-xs text-blue-600">Registered</div>
              </div>
              <div className="text-center p-3 bg-emerald-50 rounded-xl">
                <div className="text-2xl font-bold text-emerald-700">{overview?.agents?.activeWeek || 0}</div>
                <div className="text-xs text-emerald-600">Active (7d)</div>
              </div>
              <div className="text-center p-3 bg-amber-50 rounded-xl">
                <div className="text-2xl font-bold text-amber-700">{overview?.agents?.activeToday || 0}</div>
                <div className="text-xs text-amber-600">Today</div>
              </div>
            </div>
            {adoption?.agents?.slice(0, 3).map(agent => (
              <div key={agent.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-amber-500 rounded-full flex items-center justify-center text-white font-bold text-xs">
                    {agent.name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-900">{agent.name}</div>
                    <div className="text-xs text-slate-500">{agent.clientCount || 0} clients</div>
                  </div>
                </div>
                <div className="text-xs text-slate-500">
                  {agent.lastLoginAt ? formatTimeAgo(agent.lastLoginAt) : 'Never'}
                </div>
              </div>
            ))}
          </SectionCard>

          {/* Client Data Quality */}
          <SectionCard
            title="Client Data Quality"
            subtitle={`${clientQuality?.overallScore || 0}% overall score`}
            icon={<BarChart3 className="w-5 h-5 text-emerald-600" />}
            onClick={() => navigate('/founder/client-quality')}
            actionLabel="View Details"
          >
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-600 text-sm">Overall Completeness</span>
                <span className="text-2xl font-bold text-slate-900">{clientQuality?.overallScore || 0}%</span>
              </div>
              <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    (clientQuality?.overallScore || 0) >= 70 ? 'bg-emerald-500' :
                    (clientQuality?.overallScore || 0) >= 40 ? 'bg-amber-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${clientQuality?.overallScore || 0}%` }}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {clientQuality?.fields && Object.entries(clientQuality.fields).slice(0, 4).map(([key, data]) => (
                <div key={key} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                  <span className="text-xs text-slate-600">{formatQualityLabel(key)}</span>
                  <span className={`text-sm font-semibold ${
                    data.percent >= 70 ? 'text-emerald-600' : data.percent >= 40 ? 'text-amber-600' : 'text-red-600'
                  }`}>{data.percent}%</span>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Value Delivery - Risk & Retention */}
          <SectionCard
            title="Value Delivery"
            subtitle="Risk detection & retention impact"
            icon={<Zap className="w-5 h-5 text-amber-600" />}
            onClick={() => navigate('/founder/value')}
            actionLabel="View Metrics"
          >
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <Bell className="w-4 h-4 text-amber-600" />
                  <span className="text-xs font-medium text-amber-700">Risk Alerts</span>
                </div>
                <div className="text-2xl font-bold text-slate-900">{value?.riskAlerts?.generated || 0}</div>
                <div className="text-xs text-amber-600">{value?.riskAlerts?.actedOnPercent || 0}% acted on</div>
              </div>
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs font-medium text-emerald-700">Saved vs Churned</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-emerald-600">{value?.retention?.saved || 0}</span>
                  <span className="text-slate-400">/</span>
                  <span className="text-lg font-semibold text-red-500">{value?.retention?.churned || 0}</span>
                </div>
              </div>
            </div>
            {kpis?.avg_lead_time_days > 0 && (
              <div className="flex items-center gap-2 text-sm bg-slate-50 rounded-lg p-3">
                <Clock className="w-4 h-4 text-slate-500" />
                <span className="text-slate-600">Avg lead time:</span>
                <span className="font-semibold text-slate-900">{kpis.avg_lead_time_days.toFixed(1)} days</span>
              </div>
            )}
          </SectionCard>

          {/* System Health */}
          <SectionCard
            title="System Health"
            subtitle="API & messaging status"
            icon={<Activity className="w-5 h-5 text-cyan-600" />}
            onClick={() => navigate('/founder/system-health')}
            actionLabel="View Details"
          >
            <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-slate-50">
              <div className={`w-3 h-3 rounded-full ${systemHealth?.status === 'operational' ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`}></div>
              <span className={`text-sm font-medium ${systemHealth?.status === 'operational' ? 'text-emerald-700' : 'text-red-700'}`}>
                {systemHealth?.status === 'operational' ? 'All Systems Operational' : 'Issues Detected'}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <HealthMetric label="API Uptime" value={`${systemHealth?.api?.uptime || 99.9}%`} good={systemHealth?.api?.uptime >= 99} />
              <HealthMetric label="Response Time" value={`${systemHealth?.api?.avgResponseTime || 0}ms`} good={systemHealth?.api?.avgResponseTime < 500} />
              <HealthMetric label="SMS Delivery" value={`${systemHealth?.sms?.deliveryRate || 0}%`} good={systemHealth?.sms?.deliveryRate >= 90} />
              <HealthMetric label="Email Delivery" value={`${systemHealth?.email?.deliveryRate || 0}%`} good={systemHealth?.email?.deliveryRate >= 90} />
            </div>
          </SectionCard>
        </div>

        {/* ============================================ */}
        {/* QUICK ACTIONS */}
        {/* ============================================ */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <QuickAction
            icon={<Users className="w-6 h-6" />}
            label="Agent Adoption"
            description="Onboarding progress"
            onClick={() => navigate('/founder/adoption')}
          />
          <QuickAction
            icon={<BarChart3 className="w-6 h-6" />}
            label="Client Quality"
            description="Data completeness"
            onClick={() => navigate('/founder/client-quality')}
          />
          <QuickAction
            icon={<Zap className="w-6 h-6" />}
            label="Value Delivery"
            description="Risk & retention"
            onClick={() => navigate('/founder/value')}
          />
          <QuickAction
            icon={<FileText className="w-6 h-6" />}
            label="Generate Reports"
            description="Export pilot metrics"
            onClick={() => navigate('/founder/reports')}
          />
          <QuickAction
            icon={<ExternalLink className="w-6 h-6" />}
            label="Back to Dashboard"
            description="Return to main app"
            onClick={() => navigate('/dashboard')}
          />
        </div>
      </main>
    </div>
  );
}

// ============================================
// COMPONENTS
// ============================================

function KPICard({ icon, label, value, subtitle, trend, trendInverse, color, large, sparkline }) {
  const colorStyles = {
    blue: 'bg-blue-50 border-blue-200',
    cyan: 'bg-cyan-50 border-cyan-200',
    green: 'bg-green-50 border-green-200',
    emerald: 'bg-emerald-50 border-emerald-200',
    amber: 'bg-amber-50 border-amber-200',
    violet: 'bg-violet-50 border-violet-200',
    rose: 'bg-rose-50 border-rose-200'
  };

  const iconColors = {
    blue: 'text-blue-600',
    cyan: 'text-cyan-600',
    green: 'text-green-600',
    emerald: 'text-emerald-600',
    amber: 'text-amber-600',
    violet: 'text-violet-600',
    rose: 'text-rose-600'
  };

  const getTrendDisplay = () => {
    if (trend === null || trend === undefined) return null;
    const trendNum = parseFloat(trend);
    const isPositive = trendInverse ? trendNum < 0 : trendNum > 0;
    const Icon = trendNum > 0 ? ArrowUpRight : trendNum < 0 ? ArrowDownRight : Minus;
    const colorClass = isPositive ? 'text-emerald-600' : trendNum === 0 ? 'text-slate-400' : 'text-red-600';

    return (
      <div className={`flex items-center gap-0.5 text-xs font-medium ${colorClass}`}>
        <Icon className="w-3 h-3" />
        {Math.abs(trendNum).toFixed(1)}%
      </div>
    );
  };

  return (
    <div className={`${colorStyles[color]} border rounded-xl p-4 ${large ? 'col-span-2 md:col-span-1' : ''}`}>
      <div className="flex items-start justify-between mb-2">
        <div className={`${iconColors[color]}`}>{icon}</div>
        {sparkline}
      </div>
      <div className="text-xs text-slate-500 font-medium mb-1">{label}</div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      <div className="flex items-center justify-between mt-1">
        <span className="text-xs text-slate-500">{subtitle}</span>
        {getTrendDisplay()}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    amber: 'bg-amber-50 text-amber-600 border-amber-200',
    rose: 'bg-rose-50 text-rose-600 border-rose-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    violet: 'bg-violet-50 text-violet-600 border-violet-200'
  };

  return (
    <div className={`${colors[color]} border rounded-xl p-4 text-center`}>
      <div className="flex items-center justify-center gap-1 mb-1">
        {icon}
        <span className="text-xs font-medium">{label}</span>
      </div>
      <div className="text-xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

function SectionCard({ title, subtitle, icon, children, onClick, actionLabel }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
      <div
        className={`flex items-center justify-between p-4 border-b border-slate-100 ${onClick ? 'cursor-pointer hover:bg-slate-50 transition-colors' : ''}`}
        onClick={onClick}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">{icon}</div>
          <div>
            <h3 className="text-base font-semibold text-slate-900">{title}</h3>
            {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
          </div>
        </div>
        {onClick && (
          <div className="flex items-center gap-2">
            {actionLabel && <span className="text-sm text-amber-600 font-medium hidden sm:inline">{actionLabel}</span>}
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </div>
        )}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function HealthMetric({ label, value, good }) {
  return (
    <div className={`p-3 rounded-lg border ${good ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
      <div className="text-xs font-medium text-slate-500 mb-1">{label}</div>
      <div className={`text-lg font-bold ${good ? 'text-emerald-700' : 'text-amber-700'}`}>{value}</div>
    </div>
  );
}

function QuickAction({ icon, label, description, onClick }) {
  return (
    <button
      onClick={onClick}
      className="bg-white border border-slate-200 rounded-xl p-4 text-left hover:border-amber-400 hover:shadow-md transition-all group"
    >
      <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center mb-3 group-hover:bg-amber-50 transition-colors">
        <div className="text-slate-600 group-hover:text-amber-600 transition-colors">{icon}</div>
      </div>
      <div className="font-medium text-slate-900">{label}</div>
      <div className="text-xs text-slate-500">{description}</div>
    </button>
  );
}

// Helper functions
function formatQualityLabel(key) {
  const labels = {
    profileComplete: 'Profile >80%',
    bluebutton: 'Blue Button',
    medications: 'Medications',
    planInfo: 'Plan Info',
    phone: 'Phone',
    email: 'Email',
    dob: 'Date of Birth',
    medicareId: 'Medicare ID'
  };
  return labels[key] || key;
}

function formatTimeAgo(date) {
  if (!date) return 'Never';
  const d = new Date(date);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString();
}
